import {
  PaddingProps,
  paddingFields,
} from '../../../../page-builder/puck/reusable-props/padding';
import { ComponentConfig, DropZone } from '@measured/puck';
import {
  GapProps,
  gapField,
} from '../../../../page-builder/puck/reusable-props/gap';
import {
  marginFields,
  MarginProps,
} from '../../../../page-builder/puck/reusable-props/margin';
import CustomTooltip from '../../../../components/common/tooltip';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import { boxShadows } from '../../../../page-builder/puck/reusable-props/options';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getSpanPercentage,
  resolveShowComponentField,
} from '../../../utils/helper';
import { ReduxSelectorProps } from '../../../services/constants';
import { getNumberPatternWithAuto } from '../../../utils/helper';
import { FieldLabel } from '@measured/puck';
import InputField from '../../../../components/common/input-field';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { useEffect, useRef } from 'react';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import AutoStack, {
  IAutoStackValueProps,
} from '../../../puck/reusable-props/autoStack';
export type ColumnsProps = {
  distribution: 'auto' | 'manual';
  background: {
    color: string;
    isInherit: boolean;
  };
  border: IBorderValueProps;
  autoStack: IAutoStackValueProps;
  height: string;
  columns: {
    span?: string;
  }[];
  Direction: 'column' | 'column-reverse';
  verticalAlign: 'justify-start' | 'justify-center' | 'justify-end';
  boxShadow: 'none' | 'shadow-md' | 'shadow-lg' | 'shadow-xl' | 'shadow-2xl';
} & PaddingProps &
  GapProps &
  showComponentProps &
  MarginProps;

export const Columns = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<ColumnsProps> => {
  return {
    label: (
      <CustomTooltip title="Columns">
        <span>Columns</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...paddingFields,
      ...marginFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={onChange}
            title="Border"
            showWidthProps={true}
          />
        ),
      },
      columns: {
        label: 'Columns',
        type: 'array',
        min: 1,
        getItemSummary: (_, id) => `Column ${Number(id) + 1}`,
        defaultItemProps: { span: 'auto' },
        arrayFields: {
          span: {
            label: 'Width',
            type: 'custom',
            render: ({ value, onChange }) => (
              <InputField
                value={value}
                label="Width"
                onChange={e => {
                  const newValue = getSpanPercentage(e);
                  if (newValue !== null) {
                    onChange(newValue);
                  }
                }}
              />
            ),
          },
        },
      },
      verticalAlign: {
        label: 'Vertical Align',
        type: 'select',
        options: [
          { label: 'Start', value: 'justify-start' },
          { label: 'Center', value: 'justify-center' },
          { label: 'End', value: 'justify-end' },
        ],
      },
      ...gapField,
      boxShadow: {
        label: 'Shadow',
        type: 'select',
        options: boxShadows,
      },
      height: {
        type: 'custom',
        label: 'Height',
        render: ({ value, onChange }) => (
          <FieldLabel
            label={
              (
                <CustomTooltip title="Column Height">
                  <span>Column Height</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <InputField
              value={value}
              onChange={e => {
                const newValue = getNumberPatternWithAuto(e);
                if (newValue !== null) {
                  onChange(newValue);
                }
              }}
            />
          </FieldLabel>
        ),
      },
      Direction: {
        type: 'radio',
        options: [
          { label: 'Straight', value: 'column' },
          { label: 'Reverse', value: 'column-reverse' },
        ],
      },
      distribution: {
        label: 'Distribution',
        type: 'radio',
        options: [
          {
            value: 'auto',
            label: 'Auto',
          },
          {
            value: 'manual',
            label: 'Manual',
          },
        ],
      },
      autoStack: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <AutoStack title="Auto Stack" value={value} onChange={onChange} />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      boxShadow: 'none',
      verticalAlign: 'justify-start',
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      ...borderProps,
      gap: 'gap-2',
      distribution: 'auto',
      columns: [{ span: 'auto' }, { span: 'auto' }],
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      Direction: 'column',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      height: '128',
      autoStack: {
        autoStack: { mobile: true, tablet: true, desktop: false },
        deviceType: 'desktop',
      },
    },
    resolveFields: ({ props }, { fields }) =>
      resolveShowComponentField(props.id, fields),
    render: ({
      columns,
      distribution,
      Direction,
      boxShadow,
      padding,
      gap,
      margin,
      border,
      background,
      showComponent,
      height,
      verticalAlign,
      autoStack,
      id,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const observerRef = useRef<MutationObserver | null>(null);

      useEffect(() => {
        const iframe = document.getElementById(
          'preview-frame',
        ) as HTMLIFrameElement;
        const hasViewScrollbar =
          document?.documentElement.scrollHeight >
          document?.documentElement.clientHeight;
        const viewHeight = document?.querySelector('.dropzone-height');
        if (hasViewScrollbar && viewHeight) {
          (viewHeight as HTMLElement).style.removeProperty('height');
        } else if (viewHeight) {
          (viewHeight as HTMLElement).style.height = '100vh';
        }
        const checkScroll = () => {
          if (iframe?.contentDocument) {
            const hasScrollbar =
              iframe?.contentDocument.documentElement.scrollHeight >
              iframe?.contentDocument.documentElement.clientHeight;
            const iframeDocument =
              iframe.contentDocument || iframe.contentWindow?.document;
            const dropzoneHeight =
              iframeDocument?.querySelector('.dropzone-height');

            if (hasScrollbar && dropzoneHeight) {
              (dropzoneHeight as HTMLElement).style.removeProperty('height');
            } else if (dropzoneHeight) {
              (dropzoneHeight as HTMLElement).style.height = '100vh';
            }
          }
        };
        observerRef.current = new MutationObserver(checkScroll);
        observerRef.current.observe(document.body, {
          childList: true,
          subtree: true,
        });
        window.addEventListener('resize', checkScroll);

        setTimeout(() => {
          if (iframe?.contentDocument) {
            const childDiv = iframe?.contentDocument?.querySelector(
              `div[data-puck-component='${id}']`,
            ) as HTMLElement;
            if (childDiv) {
              if (isNaN(+height)) {
                childDiv.style.height = editMode ? height : 'inherit';
              } else {
                childDiv.style.removeProperty('height');
              }
            }
          }
        }, 100);
      }, [height]);
      return (
        <div
          className={`relative ${gap} ${autoStack?.autoStack?.[currentDeviceType] ? 'flex' : 'grid'} ${boxShadow} ${border?.borderStyle?.[currentDeviceType]} ${showComponent?.includes(currentDeviceType) ? '' : '!hidden'}`}
          style={{
            gridTemplateColumns:
              distribution === 'manual'
                ? columns
                    ?.map(col => {
                      if (!col?.span?.includes('%') && col?.span !== 'auto') {
                        return `${col?.span}px`;
                      }
                      return col?.span;
                    })
                    .join(' ')
                : `repeat(${columns.length}, 1fr)`,
            flexDirection: Direction,
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
            height: editMode
              ? isNaN(+height)
                ? '100%'
                : +height
              : isNaN(+height)
                ? height
                : +height,
            ...marginStyles,
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType, true),
          }}>
          {columns.map((_, idx) => (
            <DropZone
              zone={`column-${idx}`}
              key={idx}
              className={`${verticalAlign}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            />
          ))}
        </div>
      );
    },
  };
};
